/**
 * Form values to the create/update request.
 *
 * The inverse of `triggerRecordToFormValues`. Everything an invoke-action
 * target shares with the manual invoke form comes from `buildInvocationSpec`;
 * what is added here is the trigger's own vocabulary -- the file patterns the
 * change gates on, and the extra inputs a stored trigger arrived with.
 */

import { buildInvocationSpec } from "@/shared/components/invocationSpec/buildInvocationSpec";
import { type InvocationInput } from "@/shared/domain/actions";
import {
  ACCEPT_ALL_PATTERN,
  scheduleToUtcCron,
  TRIGGERING_SESSION_SELECTOR,
  TriggerSourceType,
  TriggerTargetType,
  type CreateTriggerRequest,
  type TriggerSource,
  type TriggerTargetSpec,
  type TriggerRecord,
} from "@/shared/domain/triggers";

import { completeCondition, valuesToCondition } from "./conditionValues";
import { carriesADataset, suppliedInputFor } from "./formScope";
import {
  targetSchema,
  type TargetFormOutput,
  type TriggerFormOutput,
  type TriggerFormValues,
} from "./triggerFormSchema";

/** The source as the wire states it; the entity is form-only and stays behind. */
function sourceOf(firesOn: TriggerFormValues["firesOn"]): TriggerSource {
  return firesOn.type === TriggerSourceType.Schedule
    ? {
        type: TriggerSourceType.Schedule,
        cron: scheduleToUtcCron(firesOn) ?? "",
      }
    : {
        type: TriggerSourceType.Events,
        events: firesOn.events,
        once_per: firesOn.oncePer,
      };
}

/** The request the form amounts to, `enabled` included. */
export function buildTriggerRequest(
  values: TriggerFormOutput,
): CreateTriggerRequest {
  const { firesOn } = values;
  return {
    name: values.name,
    enabled: values.enabled,
    fires_on: sourceOf(firesOn),
    // A schedule has nothing to condition on; whatever condition the events
    // draft held is dropped rather than carried across invisibly.
    condition:
      firesOn.type === TriggerSourceType.Schedule
        ? null
        : valuesToCondition(values.condition),
    targets: values.targets.map((target) => targetToSpec(target, firesOn)),
  };
}

/**
 * The patterns a file firing is gated by.
 *
 * `required_inputs` does two jobs on the wire: on a file firing it decides
 * whether the changed file fires the trigger at all, and the per-file gate
 * matches *any* pattern, so an empty list would match nothing and "every
 * file" has to be spelled out. On a dataset firing it was also the mount,
 * which is `invocation_input`'s job now -- it sends none, and the dataset
 * precondition passes on an empty list.
 */
function requiredInputsOf(
  target: Extract<TargetFormOutput, { type: TriggerTargetType.InvokeAction }>,
  firesOn: TriggerFormOutput["firesOn"],
): string[] {
  if (suppliedInputFor(firesOn) !== "changed-file") {
    return [];
  }
  return target.requiredInputs.length === 0
    ? [ACCEPT_ALL_PATTERN]
    : target.requiredInputs;
}

/**
 * The target's `invocation_input`, omitted when it selects nothing. A session
 * firing always passes its session, ahead of whatever the rows select.
 */
function invocationInputOf(
  rows: InvocationInput | null,
  firesOn: TriggerFormOutput["firesOn"],
): { invocation_input?: InvocationInput } {
  if (suppliedInputFor(firesOn) === "changed-session") {
    return {
      invocation_input: {
        files: rows?.files ?? null,
        topics: null,
        sessions: TRIGGERING_SESSION_SELECTOR,
      },
    };
  }
  return rows === null ? {} : { invocation_input: rows };
}

function targetToSpec(
  target: TargetFormOutput,
  firesOn: TriggerFormOutput["firesOn"],
): TriggerTargetSpec {
  switch (target.type) {
    case TriggerTargetType.InvokeAction: {
      const shared = buildInvocationSpec(target.spec);
      return {
        type: target.type,
        target_id: target.targetId,
        action: shared.action,
        required_inputs: requiredInputsOf(target, firesOn),
        // The server refuses these unless every subscribed event carries a
        // dataset, so they are dropped rather than sent to fail.
        ...(carriesADataset(firesOn) && target.storedAdditionalInputs.length > 0
          ? { additional_inputs: target.storedAdditionalInputs }
          : {}),
        ...invocationInputOf(shared.invocationInput, firesOn),
        parameter_values: shared.parameter_values,
        compute_requirement_overrides: shared.compute_requirement_overrides,
        container_parameter_overrides: shared.container_parameter_overrides,
        timeout: shared.timeout,
        ...(shared.upload_destination !== null
          ? { upload_destination: shared.upload_destination }
          : {}),
      };
    }
    case TriggerTargetType.StartAgent:
      return {
        type: target.type,
        target_id: target.targetId,
        agent_id: target.agentId,
        values: Object.fromEntries(
          Object.entries(target.variables).filter(
            ([, value]) => value.trim().length > 0,
          ),
        ),
        visibility: target.visibility,
        analysis_scope: target.analysisScope,
      };
    case TriggerTargetType.SendSlackMessage:
      return {
        type: target.type,
        target_id: target.targetId,
        channel_id: target.channelId,
        text: target.text,
      };
  }
}

/**
 * The record the rule preview reads: the source and the condition as they
 * stand, and only the targets that already parse, so a card with no action
 * chosen yet is simply absent from the sentence.
 */
export function previewTriggerRecord(
  values: TriggerFormValues,
): Pick<TriggerRecord, "fires_on" | "condition" | "targets"> {
  const { firesOn } = values;
  const targets: TriggerTargetSpec[] = [];
  for (const target of values.targets) {
    const parsed = targetSchema.safeParse(target);
    if (parsed.success) {
      targets.push(targetToSpec(parsed.data, firesOn));
    }
  }
  return {
    fires_on: sourceOf(firesOn),
    condition:
      firesOn.type === TriggerSourceType.Schedule
        ? null
        : completeCondition(values.condition),
    targets,
  };
}

/**
 * One target as the wire would store it, from the form as it stands, or null
 * while that target does not parse yet.
 */
export function previewTargetSpec(
  values: TriggerFormValues,
  index: number,
): TriggerTargetSpec | null {
  const parsed = targetSchema.safeParse(values.targets[index]);
  return parsed.success ? targetToSpec(parsed.data, values.firesOn) : null;
}
